import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { api, unitImages } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { enUS, de } from "date-fns/locale";
import { toast } from "sonner";

export default function BookPage() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const { search } = useLocation();

  const params = new URLSearchParams(search);
  const initialCI = params.get("ci");
  const initialCO = params.get("co");
  const initialG = parseInt(params.get("g") || "2", 10);
  const initialP = parseInt(params.get("p") || "0", 10);
  const initialUnit = params.get("unit") || "zirben";
  const cancelled = params.get("cancelled") === "1";

  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState(initialUnit);
  const [range, setRange] = useState(
    initialCI && initialCO ? { from: new Date(initialCI), to: new Date(initialCO) } : undefined
  );
  const [guests, setGuests] = useState(initialG);
  const [pets, setPets] = useState(initialP);
  const [quote, setQuote] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const activeUnit = useMemo(() => units.find((u) => u.id === unitId), [units, unitId]);
  const maxG = activeUnit?.max_guests || 8;
  const maxP = activeUnit?.max_pets ?? 2;

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { if (cancelled) toast.error(t.booking.cancelled); }, [cancelled, t]);

  useEffect(() => { api.get("/units").then(({ data }) => setUnits(data.units || [])).catch((err) => console.error("Failed to load units:", err)); }, []);

  useEffect(() => {
    api.get(`/availability/booked-dates?unit_id=${unitId}`).then(({ data }) => {
      setBookedDates((data?.dates || []).map((d) => new Date(d)));
    }).catch((err) => console.error("Failed to load booked dates:", err));
  }, [unitId]);

  useEffect(() => {
    if (range?.from && range?.to) {
      api.post("/quote", {
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        guests, pets, unit_id: unitId,
      }).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
    } else { setQuote(null); }
  }, [range, guests, pets, unitId]);

  useEffect(() => { if (pets > maxP) setPets(maxP); }, [maxP, pets]);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) { toast.error(lang === "de" ? "Bitte Zeitraum wählen" : "Pick dates"); return; }
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error(lang === "de" ? "Bitte alle Pflichtfelder ausfüllen" : "Please fill all required fields"); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        ...form,
        check_in: format(range.from, "yyyy-MM-dd"),
        check_out: format(range.to, "yyyy-MM-dd"),
        guests, pets, unit_id: unitId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(" ") : "Error");
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  const locale = lang === "de" ? de : enUS;
  const fmt = (d) => d ? format(d, "dd MMM yyyy", { locale }) : "—";
  const img = unitImages[unitId] || unitImages.zirben;

  return (
    <main data-testid="book-page" className="bg-[hsl(var(--cream))] text-[hsl(var(--cream-foreground))] min-h-screen pt-32 pb-24">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-7">
          <div className="overline mb-4">{t.nav.book}</div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight text-[hsl(var(--foreground))]">
            {t.booking.title}
          </h1>

          <form onSubmit={submit} className="mt-12 space-y-12">
            {/* Unit selector */}
            <div>
              <div className="overline mb-4">{lang === "de" ? "Unterkunft" : "Stay"}</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[hsl(var(--foreground))]/10 border border-[hsl(var(--foreground))]/10">
                {units.map((u) => {
                  const isCombo = u.id === "combo";
                  return (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => { setUnitId(u.id); if (guests > u.max_guests) setGuests(u.max_guests); }}
                      data-testid={`book-unit-${u.id}`}
                      className={`relative text-left p-5 transition-colors ${unitId === u.id ? "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" : "bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--moss))]"}`}
                    >
                      {isCombo && (
                        <span className="absolute top-2 right-2 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-[10px] tracking-[0.18em] font-semibold uppercase px-2 py-0.5">−10%</span>
                      )}
                      <div className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${unitId === u.id ? "text-[hsl(var(--accent-foreground))]/80" : "text-[hsl(var(--accent))]"}`}>
                        {isCombo ? "Combo" : (lang === "de" ? "Haus" : "House")}
                      </div>
                      <div className="font-serif text-xl md:text-2xl mt-1 leading-tight">
                        {isCombo ? (lang === "de" ? "Beide Häuser" : "Both Houses") : u.name}
                      </div>
                      <div className={`mt-1 text-xs ${unitId === u.id ? "text-[hsl(var(--accent-foreground))]/75" : "text-[hsl(var(--foreground))]/80"}`}>
                        €{u.summer_rate_eur.toFixed(0)} {lang === "de" ? "Sommer" : "summer"} · €{u.winter_rate_eur.toFixed(0)} {lang === "de" ? "Winter" : "winter"} · max {u.max_guests}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="overline mb-6">{t.booking.summary}</div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[hsl(var(--foreground))]/10 border border-[hsl(var(--foreground))]/10">
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="book-dates" className="text-left p-5 bg-[hsl(var(--card))] hover:bg-[hsl(var(--moss))] transition-colors">
                      <div className="overline">{t.widget.checkin} → {t.widget.checkout}</div>
                      <div className="mt-2 font-serif text-xl text-[hsl(var(--foreground))]">{fmt(range?.from)} → {fmt(range?.to)}</div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!rounded-none p-0 border border-[hsl(var(--foreground))]/15 shadow-2xl bg-[hsl(var(--card))]" align="start">
                    <Calendar mode="range" selected={range} onSelect={setRange} numberOfMonths={2} locale={locale}
                      disabled={[{ before: new Date() }, ...bookedDates]} data-testid="book-calendar" />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="book-guests" className="text-left p-5 bg-[hsl(var(--card))] hover:bg-[hsl(var(--moss))] transition-colors">
                      <div className="overline">{t.widget.guests}</div>
                      <div className="mt-2 font-serif text-xl text-[hsl(var(--foreground))]">{guests}</div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!rounded-none p-4 w-56 border border-[hsl(var(--foreground))]/15 bg-[hsl(var(--card))] text-[hsl(var(--foreground))]" align="start">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{t.widget.guests}</span>
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => setGuests((g) => Math.max(1, g - 1))} className="w-8 h-8 border border-[hsl(var(--foreground))]/20 hover:bg-[hsl(var(--foreground))]/10">−</button>
                        <span className="w-6 text-center">{guests}</span>
                        <button type="button" onClick={() => setGuests((g) => Math.min(maxG, g + 1))} className="w-8 h-8 border border-[hsl(var(--foreground))]/20 hover:bg-[hsl(var(--foreground))]/10">+</button>
                      </div>
                    </div>
                    <div className="mt-2 text-[11px] text-[hsl(var(--foreground))]/78">max {maxG}</div>
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="book-pets" className="text-left p-5 bg-[hsl(var(--card))] hover:bg-[hsl(var(--moss))] transition-colors">
                      <div className="overline">{t.widget.pets}</div>
                      <div className="mt-2 font-serif text-xl text-[hsl(var(--foreground))]">{pets}</div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!rounded-none p-4 w-56 border border-[hsl(var(--foreground))]/15 bg-[hsl(var(--card))] text-[hsl(var(--foreground))]" align="start">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{t.widget.pets}</span>
                      <div className="flex items-center gap-3">
                        <button type="button" onClick={() => setPets((p) => Math.max(0, p - 1))} className="w-8 h-8 border border-[hsl(var(--foreground))]/20 hover:bg-[hsl(var(--foreground))]/10">−</button>
                        <span className="w-6 text-center">{pets}</span>
                        <button type="button" onClick={() => setPets((p) => Math.min(maxP, p + 1))} className="w-8 h-8 border border-[hsl(var(--foreground))]/20 hover:bg-[hsl(var(--foreground))]/10">+</button>
                      </div>
                    </div>
                    <div className="mt-2 text-[11px] text-[hsl(var(--foreground))]/78">max {maxP} · €35 {lang === "de" ? "pro Tier" : "per pet"}</div>
                  </PopoverContent>
                </Popover>

                <div className="bg-[hsl(var(--card))] p-5">
                  <div className="overline">{t.widget.total}</div>
                  <div className="mt-2 font-serif text-2xl text-[hsl(var(--accent))]">
                    {quote ? `€${quote.total.toFixed(0)}` : "—"}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="overline mb-6">{t.booking.yourDetails}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                <label className="block">
                  <span className="overline">{t.booking.firstName} *</span>
                  <input data-testid="input-firstname" required value={form.first_name} onChange={handle("first_name")} className="input-luxe mt-2" />
                </label>
                <label className="block">
                  <span className="overline">{t.booking.lastName} *</span>
                  <input data-testid="input-lastname" required value={form.last_name} onChange={handle("last_name")} className="input-luxe mt-2" />
                </label>
                <label className="block">
                  <span className="overline">{t.booking.email} *</span>
                  <input data-testid="input-email" type="email" required value={form.email} onChange={handle("email")} className="input-luxe mt-2" />
                </label>
                <label className="block">
                  <span className="overline">{t.booking.phone} *</span>
                  <input data-testid="input-phone" required value={form.phone} onChange={handle("phone")} className="input-luxe mt-2" />
                </label>
                <label className="block md:col-span-2">
                  <span className="overline">{t.booking.message}</span>
                  <textarea data-testid="input-message" rows={3} value={form.message} onChange={handle("message")} className="input-luxe mt-2 resize-none" />
                </label>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-4">
              <button type="submit" disabled={submitting || !quote} data-testid="book-submit" className="btn-primary">
                {submitting ? t.booking.processing : t.booking.submit}
              </button>
              <button type="button" onClick={() => nav("/")} className="btn-ghost">{t.booking.cancel}</button>
            </div>
          </form>
        </div>

        <aside data-testid="book-summary" className="lg:col-span-5">
          <div className="sticky top-28 bg-[hsl(var(--card))] border border-[hsl(var(--foreground))]/10">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={img.hero} alt={activeUnit?.name || "Chalet"} className="w-full h-full object-cover" />
            </div>
            <div className="p-8">
              <div className="overline mb-3">Chalet Steiermark</div>
              <h3 data-testid="summary-unit-name" className="font-serif text-2xl text-[hsl(var(--foreground))]">{activeUnit?.name || "Zirben Chalet"}</h3>
              <p className="mt-1 text-sm text-[hsl(var(--foreground))]/80">Pusterwald · Steiermark</p>

              <div className="mt-7 space-y-2.5 text-sm">
                {quote && (
                  <>
                    {quote.summer_nights > 0 && (
                      <Row label={`${lang === "de" ? "Sommer" : "Summer"}: €${quote.summer_rate.toFixed(0)} × ${quote.summer_nights}`} value={`€${quote.summer_subtotal.toFixed(0)}`} />
                    )}
                    {quote.winter_nights > 0 && (
                      <Row label={`${lang === "de" ? "Winter" : "Winter"}: €${quote.winter_rate.toFixed(0)} × ${quote.winter_nights}`} value={`€${quote.winter_subtotal.toFixed(0)}`} />
                    )}
                    <Row label={t.widget.cleaning} value={`€${quote.cleaning_fee.toFixed(0)}`} />
                    {quote.pets > 0 && (
                      <Row label={`${t.widget.pet_fee} (${quote.pets} × €${quote.pet_fee_per_animal.toFixed(0)})`} value={`€${quote.pet_fee_total.toFixed(0)}`} />
                    )}
                    <div className="pt-4 mt-2 border-t border-[hsl(var(--foreground))]/10 flex items-baseline justify-between gap-4">
                      <div className="font-serif text-xl text-[hsl(var(--foreground))]">{t.widget.total}</div>
                      <div className="font-serif text-3xl text-[hsl(var(--accent))]" data-testid="summary-total">€{quote.total.toFixed(0)}</div>
                    </div>
                  </>
                )}
                {!quote && <p className="text-[hsl(var(--foreground))]/80">
                  {lang === "de" ? "Wähle deinen Zeitraum, um den Preis zu sehen." : "Pick your dates to see the price."}
                </p>}
              </div>

              {activeUnit?.owner && (
                <div className="mt-6 pt-5 border-t border-[hsl(var(--foreground))]/10 text-xs text-[hsl(var(--foreground))]/88">
                  <div className="overline mb-1">{lang === "de" ? "Vermieter" : "Host"}</div>
                  <div data-testid="summary-owner" className="text-[hsl(var(--foreground))]">{activeUnit.owner}</div>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between">
      <div className="text-[hsl(var(--foreground))]/80">{label}</div>
      <div className="text-[hsl(var(--foreground))]">{value}</div>
    </div>
  );
}
